/**
 * `/admin/people` — admin people list with filter tabs, substring search,
 * and pagination (M13 slice).
 *
 * Consumes `GET /people` (`ApiClient.adminListPeople`). Filter tabs map
 * 1:1 to the `status` query param; "All" omits the filter so the server
 * returns every non-deleted row. The search input is a substring match
 * against `full_name` + `contact_email` — PRD 06 calls for a "fuzzy"
 * filter and the M7 backend already does that via an `ILIKE '%q%'`
 * predicate against both columns.
 *
 * Pagination is server-driven — we pass `page` + `page_size` and render
 * the response's `pagination` envelope (total_items, total_pages) so a
 * filter change always resets to page 1.
 *
 * 401 from any call redirects back to `/admin/sign-in`; non-401 errors
 * surface an inline alert with a Retry affordance. Clicking a row
 * navigates to `/admin/people/:personId`, the next M13 slice. A "Back
 * to dashboard" link points at `/admin`.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client.js';
import type { ApiClient } from '../../api/client.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import type { PeopleListResponse, Person, PersonStatus, PersonType } from '@isms/shared';

export interface PeopleListPageProps {
  client: ApiClient;
  /** Where to send the admin on 401. Defaults to `/admin/sign-in`. */
  signInPath?: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; error: ApiError | string }
  | { kind: 'ready'; data: PeopleListResponse };

type StatusFilter = PersonStatus | 'all';

interface FilterTab {
  value: StatusFilter;
  label: string;
}

const FILTER_TABS: FilterTab[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'stood_down', label: 'Stood down' },
  { value: 'inactive', label: 'Inactive' },
];

const STATUS_LABELS: Record<PersonStatus, string> = {
  active: 'Active',
  onboarding: 'Onboarding',
  inactive: 'Inactive',
  stood_down: 'Stood down',
};

const PERSON_TYPE_LABELS: Record<PersonType, string> = {
  volunteer: 'Volunteer',
  practicum_student: 'Practicum student',
  observer: 'Observer',
  staff: 'Staff',
};

const PAGE_SIZE = 25;

export function PeopleListPage({
  client,
  signInPath = '/admin/sign-in',
}: PeopleListPageProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const redirectToSignIn = useCallback(() => {
    navigate(signInPath, { replace: true });
  }, [navigate, signInPath]);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const data = await client.adminListPeople({
        page,
        page_size: PAGE_SIZE,
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        ...(appliedSearch ? { q: appliedSearch } : {}),
      });
      setState({ kind: 'ready', data });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        redirectToSignIn();
        return;
      }
      setState({
        kind: 'error',
        error:
          err instanceof ApiError
            ? err
            : "We couldn't load the people list. Please check your connection and try again.",
      });
    }
  }, [client, statusFilter, appliedSearch, page, redirectToSignIn]);

  useEffect(() => {
    void load();
  }, [load]);

  function handleFilterChange(value: StatusFilter): void {
    setStatusFilter(value);
    setPage(1);
  }

  function handleSearchSubmit(e: FormEvent): void {
    e.preventDefault();
    const trimmed = searchInput.trim();
    setAppliedSearch(trimmed);
    setPage(1);
  }

  function handleClearSearch(): void {
    setSearchInput('');
    setAppliedSearch('');
    setPage(1);
  }

  function handleRowClick(personId: string): void {
    navigate(`/admin/people/${encodeURIComponent(personId)}`);
  }

  return (
    <main className="admin-people" aria-labelledby="admin-people-heading">
      <nav className="admin-people-breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin">← Back to dashboard</Link>
      </nav>

      <header className="admin-people-header">
        <h1 id="admin-people-heading">People</h1>
        <p className="admin-people-subtitle">
          Volunteers, practicum students, observers, and staff registered in the compliance
          register.
        </p>
        <Link to="/admin/people/new" className="admin-people-add-button">
          Add person
        </Link>
      </header>

      <div className="admin-people-toolbar">
        <div className="admin-people-filter-tabs" role="tablist" aria-label="Filter by status">
          {FILTER_TABS.map((tab) => {
            const isActive = tab.value === statusFilter;
            return (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`admin-people-filter-tab ${isActive ? 'is-active' : ''}`}
                onClick={() => handleFilterChange(tab.value)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <form className="admin-people-search" onSubmit={handleSearchSubmit} role="search">
          <label htmlFor="admin-people-search-input" className="admin-people-search-label">
            Search
          </label>
          <input
            id="admin-people-search-input"
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name or email"
            className="admin-people-search-input"
          />
          <button type="submit" className="admin-people-search-submit">
            Apply
          </button>
          {appliedSearch ? (
            <button type="button" className="admin-people-search-clear" onClick={handleClearSearch}>
              Clear
            </button>
          ) : null}
        </form>
      </div>

      <PeopleListBody
        state={state}
        onRetry={() => void load()}
        onRowClick={handleRowClick}
        onPageChange={(next) => setPage(next)}
      />
    </main>
  );
}

interface PeopleListBodyProps {
  state: LoadState;
  onRetry: () => void;
  onRowClick: (personId: string) => void;
  onPageChange: (page: number) => void;
}

function PeopleListBody({
  state,
  onRetry,
  onRowClick,
  onPageChange,
}: PeopleListBodyProps): JSX.Element {
  if (state.kind === 'loading') {
    return (
      <section className="admin-people-body" aria-busy="true">
        <p className="admin-people-loading">Loading people…</p>
      </section>
    );
  }

  if (state.kind === 'error') {
    return (
      <section className="admin-people-body">
        <div className="admin-people-error">
          <ErrorBanner error={state.error} />
          <button type="button" onClick={onRetry}>
            Retry
          </button>
        </div>
      </section>
    );
  }

  const { items, pagination } = state.data;

  if (items.length === 0) {
    return (
      <section className="admin-people-body">
        <p className="admin-people-empty">No people match your filters.</p>
      </section>
    );
  }

  return (
    <section className="admin-people-body">
      <p className="admin-people-summary">
        Showing {items.length} of {pagination.total_items}{' '}
        {pagination.total_items === 1 ? 'person' : 'people'}
      </p>

      <ul className="admin-people-list">
        {items.map((person) => (
          <PersonRow key={toIdString(person.person_id)} person={person} onClick={onRowClick} />
        ))}
      </ul>

      <PaginationControls pagination={pagination} onPageChange={onPageChange} />
    </section>
  );
}

interface PersonRowProps {
  person: Person;
  onClick: (personId: string) => void;
}

function PersonRow({ person, onClick }: PersonRowProps): JSX.Element {
  const id = toIdString(person.person_id);
  return (
    <li className="admin-people-row">
      <button
        type="button"
        className="admin-people-row-button"
        onClick={() => onClick(id)}
        aria-label={`Open person detail for ${person.full_name}`}
      >
        <span className="admin-people-row-name">
          {person.full_name}
          {person.preferred_name ? (
            <span className="admin-people-row-preferred"> ({person.preferred_name})</span>
          ) : null}
        </span>
        <span className="admin-people-row-meta">
          <span className="admin-people-row-email">{person.contact_email}</span>
          <span className="admin-people-row-type">{PERSON_TYPE_LABELS[person.person_type]}</span>
          <StatusBadge status={person.status} />
          {person.direct_child_contact ? (
            <span className="admin-people-row-child-contact">Direct child contact</span>
          ) : null}
        </span>
      </button>
    </li>
  );
}

interface StatusBadgeProps {
  status: PersonStatus;
}

function StatusBadge({ status }: StatusBadgeProps): JSX.Element {
  return (
    <span className={`admin-people-status admin-people-status-${status}`}>
      {STATUS_LABELS[status]}
    </span>
  );
}

interface PaginationControlsProps {
  pagination: PeopleListResponse['pagination'];
  onPageChange: (page: number) => void;
}

function PaginationControls({
  pagination,
  onPageChange,
}: PaginationControlsProps): JSX.Element | null {
  if (pagination.total_pages <= 1) {
    return null;
  }
  const prevDisabled = pagination.page <= 1;
  const nextDisabled = pagination.page >= pagination.total_pages;
  return (
    <nav className="admin-people-pagination" aria-label="Pagination">
      <button
        type="button"
        onClick={() => onPageChange(pagination.page - 1)}
        disabled={prevDisabled}
      >
        Previous
      </button>
      <span className="admin-people-pagination-label" aria-live="polite">
        Page {pagination.page} of {pagination.total_pages}
      </span>
      <button
        type="button"
        onClick={() => onPageChange(pagination.page + 1)}
        disabled={nextDisabled}
      >
        Next
      </button>
    </nav>
  );
}

function toIdString(id: Person['person_id']): string {
  return typeof id === 'string' ? id : String(id);
}
